import React, { useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { useReactToPrint } from 'react-to-print';
import { MdFileDownload } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { theme } from 'lib/style';
import { useReportData } from 'lib/const';

import {
  AreaChart,
  calculateDatesForRange,
  DateRangeSelector,
} from '../../components';
import { HelpDesk } from 'lib/components/helpDesk';
import { EHelpDesk } from 'lib/components/helpDesk/utils';
import { Gap, HeaderWrapper, MainWrapper } from 'lib/components/styles/layout';
import { Heading } from 'lib/components/styles/typography';
import { getDateRangeFromLocalstorage } from '../../components/DateRangeSelector/DateRangeSelector';
import { useAuth } from 'lib/context';
import { Button } from 'react-covideo-common';
import { MostViewedVideos } from '../../dashboard/main/widgets/MostViewedVideos';
import { BestPerformingCtas } from '../../dashboard/main/widgets/BestPerformingCtas';
import { useTranslation } from 'react-i18next';
import { timeRangeConstants } from '../../components/DateRangeSelector/useTimeRangeOptions';
import { Field } from 'lib/components/modal/ModalCustomTableFields/types';
import { ButtonWithTableFieldsModal } from 'lib/components/buttonWithTableFieldsModal';

const Container = styled(MainWrapper)`
  @media print {
    padding: 20px;
    @page {
      size: portrait;
    }
  }
  .dropdown-container {
    margin: 0;
  }
`;

const Content = styled.div`
  display: block;
  margin-top: 16px;
  ${theme.mediaQueryMinWidth.lg} {
    display: flex;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  ${theme.mediaQueryMinWidth.lg} {
    width: 66%;
  }
  @media print {
    display: block;
  }
`;

const ChartGroup = styled.div`
  display: flex;
  width: 100%;
  height: 400px;
  box-sizing: border-box;
  ${theme.mediaQueryMinWidth.lg} {
    width: 50%;
  }
  @media print {
    width: 50%;
    display: inline-block;
    page-break-after: always;
  }
  > div {
    border-radius: 4px;
    box-shadow: 0 4px 12px 0 #1d1e240a;
    border: solid 1px #f7f8fa;
    margin: 16px 32px 16px 0;
    @media print {
      box-shadow: none;
      border: none;
    }
  }
`;

const ListContainer = styled.div`
  width: 100%;
  > div {
    margin: 16px 0 32px 0;
  }
  @media print {
    display: none;
  }
  ${theme.mediaQueryMinWidth.lg} {
    width: 34%;
  }
`;

const sectionName = 'Overview';

export const ReportsOverview = () => {
  const { t } = useTranslation();
  const { reportData } = useReportData();
  const location = useLocation();
  const [reports, setReports] = useState<Field[]>([]);
  const queryParams: any = queryString.parse(location.search);

  const storedDateRange = getDateRangeFromLocalstorage();
  const initRange =
    queryParams.range ||
    storedDateRange?.range ||
    timeRangeConstants.LAST_7_DAYS;
  const { start, end } = calculateDatesForRange(
    initRange,
    storedDateRange?.start,
    storedDateRange?.end
  );
  const { userData } = useAuth();

  const allReports: Field[] = useMemo(
    () =>
      Object.keys(reportData)
        .filter(reportValue => {
          const report = reportData[reportValue];

          const shouldHideAutomotive =
            report.isAutomotive && !userData?.isAutomotiveServiceRole;

          return !report.hideInMyStats && !shouldHideAutomotive;
        })
        .map(reportValue => ({
          value: reportValue,
          label: reportData[reportValue].label,
          description: reportData[reportValue]?.description || '',
        })),
    [reportData, userData?.isAutomotiveServiceRole]
  );

  const defaultReports = React.useMemo<Field[]>(
    () => allReports.slice(0, 4),
    [allReports]
  );

  const [startDate, setStartDate] = React.useState<Date>(start);
  const [endDate, setEndDate] = React.useState<Date>(end);
  const [dateRange, setDateRange] = React.useState(initRange);
  const chartContainerRef = React.useRef(null);
  const handleDownload = useReactToPrint({
    contentRef: chartContainerRef,
  });

  const onDateRangeChange = (start: Date, end: Date, range: string) => {
    setStartDate(start);
    setEndDate(end);
    setDateRange(range);
  };

  const onSaveCustomTableFields = (fields: Field[]) => {
    setReports(fields);
  };

  return (
    <Container ref={chartContainerRef} resetPadding={true}>
      <HelpDesk name={EHelpDesk.MY_STATS} />
      <Heading m='0 0 32px 0'>{t('My Stats')}</Heading>
      <HeaderWrapper>
        <DateRangeSelector
          initialValue={dateRange}
          onDateRangeChange={onDateRangeChange}
        />
        <Gap>
          <Button
            text={t('Download')}
            icon={<MdFileDownload size={20} />}
            onClick={handleDownload}
          />
          <ButtonWithTableFieldsModal
            allFields={allReports}
            defaultReports={defaultReports}
            onUpdateClick={onSaveCustomTableFields}
            storageKey={sectionName}
            setReports={setReports}
            reports={reports}
          />
        </Gap>
      </HeaderWrapper>
      <Content>
        <ChartContainer>
          {reports.map(report => (
            <ChartGroup key={report.value}>
              <AreaChart
                reportType={report.value}
                from={startDate}
                to={endDate}
                dateRange={dateRange}
              />
            </ChartGroup>
          ))}
        </ChartContainer>
        <ListContainer>
          <MostViewedVideos
            from={startDate}
            to={endDate}
            label='Most Viewed Videos'
          />
          <BestPerformingCtas
            from={startDate}
            to={endDate}
            label='Best Performing CTAs'
          />
        </ListContainer>
      </Content>
    </Container>
  );
};
